import React, { useEffect, useMemo, useState } from 'react';
import { Table, Container, Pagination, Spinner, Alert, Button, ButtonGroup } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import InlineNotice from '../components/InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import usePermissions from '../utils/usePermissions';
import ProductModal from '../components/ProductModal';
import ConfirmDialog from '../components/ConfirmDialog';
import ListSearchBar from '../components/ListSearchBar';
import useDebouncedValue from '../utils/useDebouncedValue';
import { getActiveSearchTerm, shouldTriggerSearchRequest } from '../utils/searchThreshold';
import '../styles/CustomerList.css'; // We can reuse the same styles
import { formatCurrency as formatCurrencyBySystem, getCurrencyPrefixFromStorage, getNumberingSystemFromStorage } from '../utils/numberingSystem';

const ProductListPage = () => {
  const { can } = usePermissions('products');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showInlineNotice } = useInlineNotice();
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    column: 'slno',
    direction: 'asc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  
  // Get current user role
  const permissions = useMemo(() => ({
    canCreate: can('create'),
    canEdit: can('edit'),
    canDelete: can('delete')
  }), [can]);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const activeSearchTerm = getActiveSearchTerm(debouncedSearch);
  const shouldTriggerSearch = shouldTriggerSearchRequest(debouncedSearch);
  const formatCurrency = (value) => (
    formatCurrencyBySystem(Number(value || 0), getNumberingSystemFromStorage(), {
      prefix: getCurrencyPrefixFromStorage()
    })
  );


  const fetchProducts = async (page = 1, searchTerm = '') => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/products?page=${page}&limit=${pagination.limit}`, {
        params: searchTerm ? { search: searchTerm } : undefined,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const payload = response.data || {};
      const nextProducts = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.products)
          ? payload.products
          : Array.isArray(payload.data)
            ? payload.data
            : Array.isArray(payload?.data?.products)
              ? payload.data.products
              : [];
      const rawPagination = payload.pagination || payload.meta || {};
      const nextPagination = {
        page,
        limit: pagination.limit,
        total: nextProducts.length,
        pages: 1,
        ...rawPagination
      };

      if (!nextPagination.pages && rawPagination.totalPages) {
        nextPagination.pages = rawPagination.totalPages;
      }

      setProducts(nextProducts);
      setPagination(nextPagination);
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      const errorMessage = err.response?.data?.error || err.message;
      setError(`Failed to load products: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!shouldTriggerSearch) {
      return;
    }
    setSelectedRows([]);
    fetchProducts(1, activeSearchTerm);
  }, [activeSearchTerm, shouldTriggerSearch]);

  const handlePageChange = (page) => {
    fetchProducts(page, activeSearchTerm);
  };

  const handleReload = async () => {
    setSelectedRows([]);
    await fetchProducts(pagination.page, activeSearchTerm);
  };

  const handleCheckboxChange = (productId) => {
    setSelectedRows(prevSelected => {
      if (prevSelected.includes(productId)) {
        return prevSelected.filter(id => id !== productId);
      } else {
        return [...prevSelected, productId];
      }
    });
  };

  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      column,
      direction: prevConfig.column === column && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Add new product
  const handleAddProduct = () => {
    if (!permissions.canCreate) return;
    setSelectedProduct(null);
    setShowModal(true);
  };

  // View selected product
  const handleViewProduct = () => {
    if (selectedRows.length === 1) {
      const product = products.find(p => p._id === selectedRows[0]);
      setSelectedProduct(product);
      setShowModal(true);
    }
  };

  // Edit selected product
  const handleEditProduct = () => {
    if (selectedRows.length === 1) {
      if (!permissions.canEdit) return;
      const product = products.find(p => p._id === selectedRows[0]);
      setSelectedProduct(product);
      setShowModal(true);
    }
  };

  // Save product (create or update)
  const handleSaveProduct = async (formData) => {
    try {
      if (selectedProduct && !permissions.canEdit) {
        setError('You do not have permission to edit products.');
        return;
      }

      if (!selectedProduct && !permissions.canCreate) {
        setError('You do not have permission to add products.');
        return;
      }

      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      let response;
      if (selectedProduct) {
        // Update existing product
        response = await axios.put(
          `${API_BASE_URL}/products/${selectedProduct._id}`,
          formData,
          { headers }
        );
        const updatedProduct = response.data;
        setProducts((prev) => prev.map((item) => (
          item._id === updatedProduct._id ? updatedProduct : item
        )));
        setSelectedProduct(updatedProduct);
        const name = response.data?.name || selectedProduct.name || 'Product';
        showInlineNotice(`Product ${name} updated successfully.`, { scope: 'products' });
      } else {
        // Create new product
        response = await axios.post(`${API_BASE_URL}/products`, formData, { headers });
        const name = response.data?.name || formData.name || 'Product';
        showInlineNotice(`Product ${name} created successfully.`, { scope: 'products' });
      }

      // Refresh the product list
      await fetchProducts(pagination.page, activeSearchTerm);
    } catch (err) {
      console.error('Error saving product:', err);
      showInlineNotice('Failed to save product.', {
        scope: 'products',
        type: 'error'
      });
      throw err;
    }
  };

  // Delete product
  const handleDeleteProduct = () => {
    if (selectedRows.length === 1 && permissions.canDelete) {
      const product = products.find(p => p._id === selectedRows[0]);
      setProductToDelete(product);
      setShowDeleteConfirm(true);
    }
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/products/${productToDelete._id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const name = productToDelete?.name || 'Product';
      showInlineNotice(`Product ${name} deleted successfully.`, { scope: 'products' });
      setSelectedRows([]);
      // Refresh the product list
      fetchProducts(pagination.page, activeSearchTerm);
      setShowDeleteConfirm(false);
      setProductToDelete(null);
    } catch (err) {
      console.error('Error deleting product:', err);
      setError(err.response?.data?.error || 'Failed to delete product');
      showInlineNotice('Failed to delete product.', {
        scope: 'products',
        type: 'error'
      });
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="recent-invoices">
      <div className="recent-invoices-header">
        <h1>Products <span className="text-muted">({pagination.total})</span></h1>
      </div>
      {error && <div className="recent-error">{error}</div>}
      <InlineNotice scope="products" />
      <div className="recent-invoices-list">
        <div className="recent-invoices-table-header">
          <div className="recent-invoices-actions" style={{ justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={handleReload}
                disabled={loading}
                className="recent-invoices-btn recent-invoices-btn-reload"
              >
                {loading ? (
                  <>
                    <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/><path d="M11 1a9 9 0 0 1 9 9"/></svg>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
                    Reload
                  </>
                )}
              </button>
              <button 
                onClick={handleViewProduct}
                disabled={selectedRows.length !== 1}
                className="recent-invoices-btn recent-invoices-btn-view"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                View
              </button>
              {permissions.canEdit && (
                <button 
                  onClick={handleEditProduct}
                  disabled={selectedRows.length !== 1}
                  className="recent-invoices-btn recent-invoices-btn-copy"
                  title="Edit selected product"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                  Edit
                </button>
              )}
              {permissions.canDelete && (
                <button 
                  onClick={handleDeleteProduct}
                  disabled={selectedRows.length !== 1}
                  className="recent-invoices-btn recent-invoices-btn-delete"
                  style={{ backgroundColor: '#ef4444', color: 'white' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                  Delete
                </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ListSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by Code, Description, HSN Code"
                disabled={loading}
                isSearching={loading && Boolean(activeSearchTerm)}
              />
              {permissions.canCreate && (
                <button
                  onClick={handleAddProduct}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add
                </button>
              )}
            </div>
          </div>
        </div>
      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th style={{width: '3%'}}></th>
              <th style={{width: '5%'}}>#</th>
              <th onClick={() => handleSort('slno')} style={{width: '10%', cursor: 'pointer'}}>Code {sortConfig.column === 'slno' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('description')} style={{width: '28%', cursor: 'pointer'}}>Description {sortConfig.column === 'description' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th onClick={() => handleSort('rate')} style={{width: '10%', cursor: 'pointer'}}>Price {sortConfig.column === 'rate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th style={{width: '10%'}}>HSN Code</th>
              <th onClick={() => handleSort('tax')} style={{width: '10%', cursor: 'pointer'}}>Tax (%) {sortConfig.column === 'tax' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th style={{width: '15%'}}>Created At</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, index) => (
              <tr 
                key={product._id}
                className={selectedRows.includes(product._id) ? 'selected' : ''}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(product._id)}
                    onChange={() => handleCheckboxChange(product._id)}
                  />
                </td>
                <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                <td>{product.slno || '-'}</td>
                <td>
                  <div className="product-info-cell">
                    <div className="product-name">{product.description}</div>
                  </div>
                </td>
                <td>
                  {formatCurrency(product.rate)}
                </td>
                <td>{product.hsncode || '-'}</td>
                <td>{product.tax ? `${product.tax}%` : '-'}</td>
                <td>{new Date(product.createdAt).toLocaleDateString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {pagination.pages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              disabled={pagination.page === 1}
              onClick={() => handlePageChange(1)}
            />
            <Pagination.Prev
              disabled={pagination.page === 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            />
            
            {[...Array(pagination.pages)].map((_, index) => (
              <Pagination.Item
                key={index + 1}
                active={pagination.page === index + 1}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            
            <Pagination.Next
              disabled={pagination.page === pagination.pages}
              onClick={() => handlePageChange(pagination.page + 1)}
            />
            <Pagination.Last
              disabled={pagination.page === pagination.pages}
              onClick={() => handlePageChange(pagination.pages)}
            />
          </Pagination>
        </div>
      )}
      </div>

      {/* Product Modal */}
      <ProductModal
        show={showModal}
        onHide={() => setShowModal(false)}
        product={selectedProduct}
        onSave={handleSaveProduct}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Product"
        message={`Are you sure you want to delete "${productToDelete?.slno}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default ProductListPage;